import { useEffect, useState, type DragEvent, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { MAJOR_KEY_OPTIONS, MINOR_KEY_OPTIONS } from "../../../shared/chords";
import { addSeconds, durationLabel, formatTime } from "../../../shared/time";
import type { Person, PlanItem, SongDetail } from "../../../shared/types";
import { Badge, ErrorNote } from "../../ui";
import type { ItemPatch } from "./client";
import { kindTone, KIND_LABEL, messageOf, parseDuration, splitDuration, standHref } from "./model";

export function ItemRow({ item, index, count, planId, song, people, canEdit, busy, dragging, over, onMove, onDragStart, onDragOver, onDrop, onDragEnd, onPatch, onDelete }: {
  item: PlanItem;
  index: number;
  count: number;
  planId: string;
  song: SongDetail | null | undefined;
  people: Person[];
  canEdit: boolean;
  busy: boolean;
  dragging: boolean;
  over: boolean;
  onMove: (index: number, delta: number) => void;
  onDragStart: (itemId: string) => void;
  onDragOver: (itemId: string) => void;
  onDrop: (draggedId: string, targetId: string) => void;
  onDragEnd: () => void;
  onPatch: (itemId: string, body: ItemPatch) => Promise<void>;
  onDelete: (itemId: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const className = `svc-item${dragging ? " is-dragging" : ""}${over ? " is-over" : ""}`;

  function dragStart(event: DragEvent<HTMLButtonElement>) {
    event.dataTransfer.setData("text/plain", item.id);
    event.dataTransfer.effectAllowed = "move";
    onDragStart(item.id);
  }

  return (
    <article
      className={className}
      data-kind={item.kind}
      onDragOver={(event) => { if (!canEdit) return; event.preventDefault(); onDragOver(item.id); }}
      onDrop={(event) => {
        event.preventDefault();
        const draggedId = event.dataTransfer.getData("text/plain");
        if (draggedId) onDrop(draggedId, item.id);
      }}
    >
      <div className="svc-clock">
        <strong>{formatTime(item.startsAt)}</strong>
        <span>{String(index + 1).padStart(2, "0")}</span>
      </div>
      <div className="svc-item-main">
        <h3 className="svc-item-title">{item.title}</h3>
        <div className="cluster">
          <Badge tone={kindTone(item.kind)}>{KIND_LABEL[item.kind]}</Badge>
          {item.arrangementName && <span className="faint">{item.arrangementName}</span>}
          {item.keyName && <Badge tone="violet">{item.keyName}</Badge>}
          {item.songDefaultKey && item.keyName && item.keyName !== item.songDefaultKey && <span className="faint">song default {item.songDefaultKey}</span>}
          {item.bpm ? <span className="faint">{item.bpm} bpm</span> : null}
          {item.meter ? <span className="faint">{item.meter}</span> : null}
          <span className="faint">{durationLabel(item.durationSeconds)}</span>
          {item.leaderName && <span className="faint">Led by {item.leaderName}</span>}
        </div>
        {item.description && <p className="muted svc-prose">{item.description}</p>}
        {item.attachments.length > 0 && (
          <div className="cluster">
            {item.attachments.map((file) => (
              <a key={file.id} className="badge" href={file.url} target="_blank" rel="noreferrer">{file.kind === "audio" ? "Audio" : file.kind === "chart-pdf" ? "Chart file" : "File"} · {file.label}</a>
            ))}
          </div>
        )}
      </div>
      <div className="cluster svc-actions">
        {item.songId && <Link to={`/songs/${item.songId}`}>Library</Link>}
        {item.songId && <Link className="btn small teal" to={standHref(planId, item.id)}>Stand</Link>}
        {canEdit && (
          <>
            <button className="btn small ghost" type="button" disabled={index === 0 || busy} onClick={() => onMove(index, -1)}>Earlier</button>
            <button className="btn small ghost" type="button" disabled={index === count - 1 || busy} onClick={() => onMove(index, 1)}>Later</button>
            <button className="icon-btn" type="button" draggable disabled={busy} aria-label={`Drag to reorder ${item.title}`} onDragStart={dragStart} onDragEnd={onDragEnd}>↕</button>
            <button className="btn small" type="button" aria-expanded={open} onClick={() => setOpen((value) => !value)}>{open ? "Close" : "Edit"}</button>
          </>
        )}
      </div>
      {open && canEdit && (
        <ItemEditor item={item} song={song} people={people} busy={busy} confirming={confirming} onConfirm={setConfirming} onPatch={onPatch} onDelete={onDelete} />
      )}
    </article>
  );
}

function ItemEditor({ item, song, people, busy, confirming, onConfirm, onPatch, onDelete }: {
  item: PlanItem;
  song: SongDetail | null | undefined;
  people: Person[];
  busy: boolean;
  confirming: boolean;
  onConfirm: (value: boolean) => void;
  onPatch: (itemId: string, body: ItemPatch) => Promise<void>;
  onDelete: (itemId: string) => Promise<void>;
}) {
  const parts = splitDuration(item.durationSeconds);
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description);
  const [minutes, setMinutes] = useState(String(parts.minutes));
  const [seconds, setSeconds] = useState(String(parts.seconds));
  const [leaderId, setLeaderId] = useState(item.leaderPersonId ?? "");
  const [keyName, setKeyName] = useState(item.keyName ?? "");
  const [arrangementId, setArrangementId] = useState(item.arrangementId ?? "");
  const [keyDirty, setKeyDirty] = useState(false);
  const [error, setError] = useState("");
  const arrangements = song?.arrangements ?? [];

  useEffect(() => {
    const next = splitDuration(item.durationSeconds);
    setTitle(item.title);
    setDescription(item.description);
    setMinutes(String(next.minutes));
    setSeconds(String(next.seconds));
    setLeaderId(item.leaderPersonId ?? "");
    setKeyName(item.keyName ?? "");
    setArrangementId(item.arrangementId ?? "");
    setKeyDirty(false);
    setError("");
  }, [item.id, item.title, item.description, item.durationSeconds, item.leaderPersonId, item.keyName, item.arrangementId]);

  const parsed = parseDuration(minutes, seconds);
  const major = keyChoices(MAJOR_KEY_OPTIONS, keyName.endsWith("m") ? "" : keyName);
  const minor = keyChoices(MINOR_KEY_OPTIONS, keyName.endsWith("m") ? keyName : "");

  function changeArrangement(value: string) {
    setArrangementId(value);
    const arrangement = arrangements.find((row) => row.id === value);
    if (arrangement && !keyDirty) setKeyName(arrangement.keyName);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!title.trim()) { setError("Give this row a title."); return; }
    if (parsed === null) { setError("Use a duration up to 8 hours."); return; }
    const body: ItemPatch = {
      title: title.trim(),
      description,
      durationSeconds: parsed,
      leaderPersonId: leaderId || null,
    };
    if (item.kind === "song") {
      body.keyName = keyName || null;
      body.arrangementId = arrangementId || null;
    }
    setError("");
    try {
      await onPatch(item.id, body);
    } catch (caught) {
      setError(messageOf(caught));
    }
  }

  async function remove() {
    setError("");
    try {
      await onDelete(item.id);
    } catch (caught) {
      setError(messageOf(caught));
    }
  }

  return (
    <form className="svc-editor" onSubmit={(event) => void save(event)}>
      {error && <ErrorNote>{error}</ErrorNote>}
      <div className="svc-editor-grid">
        <div className="field"><label htmlFor={`title-${item.id}`}>Title</label><input id={`title-${item.id}`} value={title} maxLength={200} onChange={(event) => setTitle(event.target.value)} /></div>
        <div className="field"><label htmlFor={`minutes-${item.id}`}>Minutes</label><input id={`minutes-${item.id}`} type="number" min={0} max={480} value={minutes} onChange={(event) => setMinutes(event.target.value)} /></div>
        <div className="field"><label htmlFor={`seconds-${item.id}`}>Seconds</label><input id={`seconds-${item.id}`} type="number" min={0} max={59} value={seconds} onChange={(event) => setSeconds(event.target.value)} /></div>
        <div className="field">
          <label htmlFor={`leader-${item.id}`}>Item leader</label>
          <select id={`leader-${item.id}`} value={leaderId} onChange={(event) => setLeaderId(event.target.value)}>
            <option value="">No leader</option>
            {people.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
            {leaderId && !people.some((person) => person.id === leaderId) && <option value={leaderId}>{item.leaderName ?? "Current leader"}</option>}
          </select>
        </div>
      </div>
      {item.kind === "song" && (
        <div className="svc-editor-grid">
          <div className="field">
            <label htmlFor={`arrangement-${item.id}`}>Arrangement</label>
            <select id={`arrangement-${item.id}`} value={arrangementId} onChange={(event) => changeArrangement(event.target.value)}>
              <option value="">No arrangement</option>
              {arrangements.map((arrangement) => <option key={arrangement.id} value={arrangement.id}>{arrangement.name} · {arrangement.keyName}</option>)}
              {arrangementId && !arrangements.some((arrangement) => arrangement.id === arrangementId) && <option value={arrangementId}>{item.arrangementName ?? "Current arrangement"}</option>}
            </select>
            {song === undefined && item.songId && <span className="faint">Loading arrangements…</span>}
            {song === null && <span className="notice">Arrangements could not be loaded. The current key can still be saved.</span>}
          </div>
          <div className="field">
            <label htmlFor={`key-${item.id}`}>Key on this plan</label>
            <select id={`key-${item.id}`} value={keyName} onChange={(event) => { setKeyDirty(true); setKeyName(event.target.value); }}>
              <option value="">No key</option>
              <optgroup label="Major">{major.map((key) => <option key={key} value={key}>{key}</option>)}</optgroup>
              <optgroup label="Minor">{minor.map((key) => <option key={key} value={key}>{key}</option>)}</optgroup>
            </select>
          </div>
        </div>
      )}
      <div className="field svc-wide">
        <label htmlFor={`note-${item.id}`}>Note</label>
        <textarea id={`note-${item.id}`} maxLength={4000} value={description} onChange={(event) => setDescription(event.target.value)} />
      </div>
      {parsed !== null && <p className="faint">This row would run {formatTime(item.startsAt)}–{formatTime(addSeconds(item.startsAt, parsed))}. Later rows move when you save.</p>}
      <div className="cluster">
        <button className="btn primary" type="submit" disabled={busy}>{busy ? "Saving…" : "Save row"}</button>
        {confirming ? (
          <>
            <span>Remove {item.title} from this plan?</span>
            <button className="btn small danger" type="button" disabled={busy} onClick={() => void remove()}>Remove</button>
            <button className="btn small ghost" type="button" onClick={() => onConfirm(false)}>Keep</button>
          </>
        ) : (
          <button className="btn small danger" type="button" onClick={() => onConfirm(true)}>Remove</button>
        )}
      </div>
    </form>
  );
}

function keyChoices(options: readonly string[], current: string): string[] {
  if (!current || options.includes(current)) return [...options];
  return [current, ...options];
}
